/**
 * Form controls.
 *
 * Every control is labelled, and its hint and error are wired with
 * `aria-describedby` so a screen reader reads them when the control takes
 * focus. An error replaces nothing: the hint stays, the error is added under
 * it, and the control is marked `aria-invalid`.
 *
 * Errors come from the API's field errors (`problem.errors[field]`) and are
 * shown on the field they belong to — never only in a toast.
 */
import { Check, Eye, EyeSlash, LockKey, MagnifyingGlass, Minus, PencilSimple, Plus, WarningCircle, X } from '@phosphor-icons/react';
import {
  forwardRef,
  useEffect,
  useId,
  useRef,
  useState,
  type ClipboardEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { formatNumber } from '@/lib/format';

import { Button } from './button';
import { Badge } from './feedback';
import { arrowStep, nextIndex } from './keys';
import { cx, ui } from './text';

/* ── Field ────────────────────────────────────────────────────────── */

export interface ControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  required?: boolean;
}

export interface FieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  required?: boolean;
  /** Marks the label "(اختياري)". Prefer marking the few optional fields over starring the many required ones. */
  optional?: boolean;
  /** Visually hide the label (it is still the accessible name). */
  hideLabel?: boolean;
  /** Receives the props to spread on the control. */
  children: (control: ControlProps) => ReactNode;
  className?: string;
}

/** For any control, including composites. The built-in fields below use it. */
export function Field({ label, hint, error, required = false, optional = false, hideLabel = false, children, className }: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy = [hint === undefined ? null : hintId, error === undefined ? null : errorId].filter(Boolean).join(' ');

  const control: ControlProps = { id };

  if (describedBy !== '') control['aria-describedby'] = describedBy;
  if (error !== undefined) control['aria-invalid'] = true;
  if (required) control.required = true;

  return (
    <div className={cx('field', className)} data-invalid={error !== undefined || undefined}>
      <label className={cx('field-label', hideLabel && 'sr-only')} htmlFor={id}>
        {label}
        {required ? (
          <span className="field-required" aria-hidden="true">
            *
          </span>
        ) : null}
        {optional ? <span className="field-optional">({ui('optional')})</span> : null}
      </label>
      {children(control)}
      {hint === undefined ? null : (
        <p className="field-hint" id={hintId}>
          {hint}
        </p>
      )}
      {error === undefined ? null : (
        <p className="field-error" id={errorId}>
          <WarningCircle size={14} weight="fill" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}

type FieldChrome = Omit<FieldProps, 'children' | 'className'>;

/* ── Text ─────────────────────────────────────────────────────────── */

export interface TextFieldProps extends FieldChrome, Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'required' | 'prefix'> {
  /** Identifiers, emails, URLs: monospace and left-to-right. */
  ltr?: boolean;
  /** Fixed text before the value, e.g. `https://` (rendered inline-start). */
  prefix?: ReactNode;
  /** Fixed text after the value, e.g. `ر.س`. */
  suffix?: ReactNode;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, hint, error, required, optional, hideLabel, ltr = false, prefix, suffix, className, type, ...input },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required ?? false} optional={optional ?? false} hideLabel={hideLabel ?? false}>
      {(control) => (
        // An LTR field lays out LTR as a whole, so `https://` sits before the value.
        <div className={cx('input-wrap', className)} data-ltr={ltr || undefined} dir={ltr ? 'ltr' : undefined}>
          {prefix === undefined ? null : <bdi className="input-affix">{prefix}</bdi>}
          <input ref={ref} className={cx('input', ltr && 'mono')} dir={ltr ? 'ltr' : undefined} type={type ?? 'text'} {...input} {...control} />
          {suffix === undefined ? null : <bdi className="input-affix">{suffix}</bdi>}
        </div>
      )}
    </Field>
  );
});

/* ── Password ─────────────────────────────────────────────────────── */

export type PasswordFieldProps = Omit<TextFieldProps, 'type' | 'ltr' | 'suffix'>;

/** With a reveal toggle. The toggle is a real button with a state-describing name. */
export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(function PasswordField(
  { label, hint, error, required, optional, hideLabel, className, prefix, ...input },
  ref,
) {
  const [visible, setVisible] = useState(false);

  return (
    <Field label={label} hint={hint} error={error} required={required ?? false} optional={optional ?? false} hideLabel={hideLabel ?? false}>
      {(control) => (
        <div className={cx('input-wrap', className)} data-ltr>
          {prefix === undefined ? null : <span className="input-affix">{prefix}</span>}
          <input ref={ref} className="input" dir="ltr" type={visible ? 'text' : 'password'} {...input} {...control} />
          <button
            type="button"
            className="input-button"
            aria-label={visible ? ui('hidePassword') : ui('showPassword')}
            aria-pressed={visible}
            onClick={() => setVisible((current) => !current)}
          >
            {visible ? <EyeSlash size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
          </button>
        </div>
      )}
    </Field>
  );
});

/* ── Select ───────────────────────────────────────────────────────── */

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectFieldProps extends FieldChrome, Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id' | 'required'> {
  options: SelectOption[];
  /** A first, empty option ("اختر…"). */
  placeholder?: string;
}

/** The native select: correct on every platform, keyboard and screen reader included. */
export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, hint, error, required, optional, hideLabel, options, placeholder, className, ...select },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required ?? false} optional={optional ?? false} hideLabel={hideLabel ?? false}>
      {(control) => (
        <select ref={ref} className={cx('input', 'select', className)} {...select} {...control}>
          {placeholder === undefined ? null : (
            <option value="" disabled={required ?? false}>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled ?? false}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
});

/* ── Textarea ─────────────────────────────────────────────────────── */

export interface TextareaFieldProps extends FieldChrome, Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id' | 'required'> {
  /** For code-like content (HTML templates, JSON): monospace, left-to-right. */
  ltr?: boolean;
}

export const TextareaField = forwardRef<HTMLTextAreaElement, TextareaFieldProps>(function TextareaField(
  { label, hint, error, required, optional, hideLabel, ltr = false, className, rows, ...textarea },
  ref,
) {
  return (
    <Field label={label} hint={hint} error={error} required={required ?? false} optional={optional ?? false} hideLabel={hideLabel ?? false}>
      {(control) => (
        <textarea
          ref={ref}
          className={cx('input', 'textarea', ltr && 'mono', className)}
          dir={ltr ? 'ltr' : undefined}
          rows={rows ?? 4}
          {...textarea}
          {...control}
        />
      )}
    </Field>
  );
});

/* ── Search ───────────────────────────────────────────────────────── */

export interface SearchInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  /** Accessible name; the placeholder is not one. */
  label: string;
  value: string;
  onChange: (value: string) => void;
}

/** A table's search box. Escape clears it; so does the × button. */
export function SearchInput({ label, value, onChange, className, ...input }: SearchInputProps) {
  const ref = useRef<HTMLInputElement>(null);

  return (
    <div className={cx('input-wrap', 'search', className)}>
      <MagnifyingGlass className="input-icon" size={18} aria-hidden="true" />
      <input
        ref={ref}
        className="input"
        type="search"
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && value !== '') {
            event.preventDefault();
            onChange('');
          }
        }}
        {...input}
      />
      {value === '' ? null : (
        <button
          type="button"
          className="input-button"
          aria-label={ui('clearSearch')}
          onClick={() => {
            onChange('');
            ref.current?.focus();
          }}
        >
          <X size={16} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

/* ── Checkbox / Switch / Radio ────────────────────────────────────── */

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  label: ReactNode;
  description?: ReactNode;
  /** Shows a dash and sets the DOM `indeterminate` flag — for a "select all" over a partial selection. */
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  /** Visually hide the label — for a cell in a matrix whose header names it. */
  hideLabel?: boolean;
}

export function Checkbox({ label, description, indeterminate = false, onChange, hideLabel = false, className, ...input }: CheckboxProps) {
  const descriptionId = useId();

  return (
    <label className={cx('check', className)} data-disabled={input.disabled || undefined}>
      <input
        type="checkbox"
        className="check-input"
        ref={(node) => {
          if (node !== null) node.indeterminate = indeterminate;
        }}
        aria-describedby={description === undefined ? undefined : descriptionId}
        onChange={(event) => onChange?.(event.target.checked)}
        {...input}
      />
      <span className="check-box" aria-hidden="true">
        {indeterminate ? <Minus size={12} weight="bold" /> : <Check size={12} weight="bold" />}
      </span>
      <span className={cx('check-text', hideLabel && 'sr-only')}>
        <span className="check-label">{label}</span>
        {description === undefined ? null : (
          <span className="check-description" id={descriptionId}>
            {description}
          </span>
        )}
      </span>
    </label>
  );
}

export interface SwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
  /** While the change is being saved. The switch stays where it was until the server agrees. */
  busy?: boolean;
  hideLabel?: boolean;
}

/**
 * An immediate setting. If the change needs a Save button, it is a Checkbox.
 * "On" is the knob at the inline end — phrased so it holds in both directions.
 */
export function Switch({ checked, onChange, label, description, disabled = false, busy = false, hideLabel = false }: SwitchProps) {
  const descriptionId = useId();

  return (
    <label className="switch" data-disabled={disabled || busy || undefined}>
      <input
        type="checkbox"
        role="switch"
        className="switch-input"
        checked={checked}
        disabled={disabled}
        aria-busy={busy || undefined}
        aria-describedby={description === undefined ? undefined : descriptionId}
        onChange={(event) => {
          if (!busy) onChange(event.target.checked);
        }}
      />
      <span className="switch-track" aria-hidden="true">
        <span className="switch-thumb" />
      </span>
      <span className={cx('check-text', hideLabel && 'sr-only')}>
        <span className="check-label">{label}</span>
        {description === undefined ? null : (
          <span className="check-description" id={descriptionId}>
            {description}
          </span>
        )}
      </span>
    </label>
  );
}

export interface RadioOption {
  value: string;
  label: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface RadioGroupProps {
  label: ReactNode;
  name: string;
  value: string;
  onChange: (value: string) => void;
  options: RadioOption[];
  hint?: ReactNode;
  error?: string | undefined;
  /** `cards` draws each option as a bordered choice — for a role picker. */
  appearance?: 'list' | 'cards';
}

export function RadioGroup({ label, name, value, onChange, options, hint, error, appearance = 'list' }: RadioGroupProps) {
  const id = useId();

  return (
    <fieldset
      className="radio-group"
      data-appearance={appearance}
      aria-describedby={[hint === undefined ? null : `${id}-hint`, error === undefined ? null : `${id}-error`].filter(Boolean).join(' ') || undefined}
      aria-invalid={error === undefined ? undefined : true}
    >
      <legend className="field-label">{label}</legend>
      <div className="radio-options">
        {options.map((option) => (
          <label key={option.value} className="check radio" data-disabled={option.disabled || undefined} data-checked={option.value === value || undefined}>
            <input
              type="radio"
              className="check-input"
              name={name}
              value={option.value}
              checked={option.value === value}
              disabled={option.disabled ?? false}
              onChange={() => onChange(option.value)}
            />
            <span className="check-box radio-box" aria-hidden="true" />
            <span className="check-text">
              <span className="check-label">{option.label}</span>
              {option.description === undefined ? null : <span className="check-description">{option.description}</span>}
            </span>
          </label>
        ))}
      </div>
      {hint === undefined ? null : (
        <p className="field-hint" id={`${id}-hint`}>
          {hint}
        </p>
      )}
      {error === undefined ? null : (
        <p className="field-error" id={`${id}-error`}>
          <WarningCircle size={14} weight="fill" aria-hidden="true" />
          {error}
        </p>
      )}
    </fieldset>
  );
}

/* ── Segmented ────────────────────────────────────────────────────── */

export interface SegmentedProps<V extends string> {
  /** Accessible name of the group. */
  label: string;
  value: V;
  onChange: (value: V) => void;
  options: { value: V; label: string; count?: number }[];
  size?: 'sm' | 'md';
}

/**
 * A filter of two to five mutually exclusive values (الكل · نشطة · موقوفة).
 * Radio semantics: one tab stop, arrow keys move and select.
 */
export function Segmented<V extends string>({ label, value, onChange, options, size = 'md' }: SegmentedProps<V>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const step = arrowStep(event, 'both');

    if (step === null) return;
    event.preventDefault();

    const next = nextIndex(step, index, options.length);
    const option = options[next];

    if (option !== undefined) {
      onChange(option.value);
      refs.current[next]?.focus();
    }
  }

  return (
    <div className="segmented" role="radiogroup" aria-label={label} data-size={size}>
      {options.map((option, index) => {
        const selected = option.value === value;

        return (
          <button
            key={option.value}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            className="segmented-item"
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            {option.label}
            {option.count === undefined ? null : <span className="segmented-count num">{option.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* ── ChipsField ───────────────────────────────────────────────────── */

export interface ChipsParsed {
  /** Values ready to become chips — already normalised (`@AYED.SA` → `ayed.sa`). */
  values: string[];
  /** Entries refused, with the reason. They stay in the input so they can be corrected rather than retyped. */
  rejected?: { value: string; error: string }[];
}

export interface ChipsMerge {
  /** The list after adding. */
  next: string[];
  added: string[];
  duplicates: string[];
  overflow: string[];
}

/**
 * Adds parsed values to a list: skips what is already there (or repeated in
 * the same paste), and stops at `max`. Pure, so the page's validation and
 * this merge can each be tested alone.
 */
export function mergeChips(current: readonly string[], values: readonly string[], max = Number.POSITIVE_INFINITY): ChipsMerge {
  const next = [...current];
  const added: string[] = [];
  const duplicates: string[] = [];
  const overflow: string[] = [];

  for (const value of values) {
    if (next.includes(value)) duplicates.push(value);
    else if (next.length >= max) overflow.push(value);
    else {
      next.push(value);
      added.push(value);
    }
  }

  return { next, added, duplicates, overflow };
}

/** The default parse: split on spaces, commas, semicolons and the Arabic comma; trim; no validation. */
export function splitChips(raw: string): ChipsParsed {
  return { values: raw.split(/[\s,;،]+/u).map((part) => part.trim()).filter((part) => part !== '') };
}

export interface ChipsFieldProps extends FieldChrome {
  values: readonly string[];
  onChange: (next: string[]) => void;
  /**
   * The validation hook: turn what was typed or pasted into values and
   * refusals. Normalise here, so a chip shows exactly what the server will
   * store. Defaults to `splitChips` (split, trim, accept everything).
   */
  parse?: (raw: string) => ChipsParsed;
  /** The most the list holds. A count «٣ من ٢٠» is shown, and the input closes when full. */
  max?: number;
  /** Identifiers (domains, emails): monospace, left-to-right, no autocapitalise or spellcheck. */
  ltr?: boolean;
  /** An example in the shape the field expects, e.g. `ayed.sa`. Not a label. */
  placeholder?: string;
  /** The add button's word. Defaults to «إضافة». */
  addLabel?: string;
  /** Said where the chips would be when there are none: «لا نطاقات بعد — يُقبل أي بريد». */
  emptyText?: ReactNode;
  /** A second hint under the entry row: how to enter several at once. */
  entryHint?: ReactNode;
  disabled?: boolean;
  /**
   * Whether the input holds text that is not a chip yet — typed and not
   * added, or refused. A form should block Save on it: saving would silently
   * drop what the operator can see on screen.
   */
  onPendingChange?: (pending: boolean) => void;
}

/**
 * An editable list of short values — allowed email domains. Enter, a comma or
 * «،» adds; a pasted list arrives whole; each chip has a named remove button;
 * additions and removals are announced. Refused entries stay in the input
 * with the reason as the field's error.
 */
export function ChipsField({
  label,
  hint,
  error,
  required = false,
  optional = false,
  hideLabel = false,
  values,
  onChange,
  parse = splitChips,
  max,
  ltr = false,
  placeholder,
  addLabel,
  emptyText,
  entryHint,
  disabled = false,
  onPendingChange,
}: ChipsFieldProps) {
  const [text, setText] = useState('');
  const [entryError, setEntryError] = useState<string | undefined>(undefined);
  const [note, setNote] = useState<string | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const pending = text.trim() !== '';

  useEffect(() => onPendingChange?.(pending), [pending, onPendingChange]);

  const separator = ui('listSeparator');
  const full = max !== undefined && values.length >= max;
  const name = typeof label === 'string' ? label : undefined;

  function commit(raw: string) {
    if (raw.trim() === '') return;

    const parsed = parse(raw);
    const merge = mergeChips(values, parsed.values, max);
    const rejected = parsed.rejected ?? [];

    if (merge.added.length > 0) {
      onChange(merge.next);
      setAnnouncement(ui('chipAdded', { values: merge.added.join(separator) }));
    }

    setText(rejected.map((entry) => entry.value).join(' '));
    setEntryError(rejected[0]?.error);

    const notes = [
      merge.duplicates.length > 0 ? ui('chipsDuplicate', { values: merge.duplicates.join(separator) }) : null,
      merge.overflow.length > 0 && max !== undefined ? ui('chipsOverflow', { max: formatNumber(max), values: merge.overflow.join(separator) }) : null,
    ].filter((line): line is string => line !== null);

    setNote(notes.length > 0 ? notes.join(' ') : null);
  }

  function remove(value: string) {
    onChange(values.filter((entry) => entry !== value));
    setAnnouncement(ui('chipRemoved', { value }));
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    // Enter adds rather than submitting the whole form; a comma is the other natural separator.
    if (event.key === 'Enter' || event.key === ',' || event.key === '،') {
      event.preventDefault();
      commit(text);
    }
  }

  function onPaste(event: ClipboardEvent<HTMLInputElement>) {
    const pasted = event.clipboardData.getData('text');

    // A list arrives whole; a single value is left to the normal typing path.
    if (/[\s,;،]/u.test(pasted.trim())) {
      event.preventDefault();
      commit(`${text} ${pasted}`);
    }
  }

  return (
    <Field label={label} hint={hint} error={entryError ?? error} required={required} optional={optional} hideLabel={hideLabel}>
      {(control) => (
        <div className="chips-field">
          {max === undefined ? null : (
            <span className="chips-count num" aria-hidden="true">
              {ui('chipsCount', { count: formatNumber(values.length), max: formatNumber(max) })}
            </span>
          )}

          {values.length === 0 ? (
            emptyText === undefined ? null : <p className="chips-empty">{emptyText}</p>
          ) : (
            <ul className="chips" aria-label={name}>
              {values.map((value) => (
                <li key={value} className="chip" data-readonly={disabled || undefined}>
                  {ltr ? (
                    <bdi className="chip-value mono" dir="ltr">
                      {value}
                    </bdi>
                  ) : (
                    <span className="chip-value">{value}</span>
                  )}
                  {disabled ? null : (
                    <button type="button" className="chip-remove" aria-label={ui('chipRemove', { value })} title={ui('chipRemove', { value })} onClick={() => remove(value)}>
                      <X size={12} weight="bold" aria-hidden="true" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {disabled ? null : (
            <div className="chips-entry">
              <div className="input-wrap" data-ltr={ltr || undefined} dir={ltr ? 'ltr' : undefined}>
                <input
                  className={cx('input', ltr && 'mono')}
                  dir={ltr ? 'ltr' : undefined}
                  type="text"
                  inputMode={ltr ? 'url' : undefined}
                  autoComplete="off"
                  autoCapitalize={ltr ? 'none' : undefined}
                  spellCheck={ltr ? false : undefined}
                  placeholder={placeholder}
                  value={text}
                  disabled={full}
                  onChange={(event) => {
                    setText(event.target.value);
                    setEntryError(undefined);
                    setNote(null);
                  }}
                  onKeyDown={onKeyDown}
                  onPaste={onPaste}
                  onBlur={() => commit(text)}
                  {...control}
                />
              </div>
              <Button icon={Plus} disabled={full || !pending} onClick={() => commit(text)}>
                {addLabel ?? ui('chipAdd')}
              </Button>
            </div>
          )}

          {disabled || entryHint === undefined ? null : <p className="field-hint">{entryHint}</p>}
          {note === null ? null : <p className="chips-note">{note}</p>}
          <span className="sr-only" aria-live="polite">
            {announcement}
          </span>
        </div>
      )}
    </Field>
  );
}

/* ── SecretField ──────────────────────────────────────────────────── */

export interface SecretFieldProps {
  label: ReactNode;
  hint?: ReactNode;
  error?: string | undefined;
  /** Whether the server already holds a value. The value itself never reaches the panel. */
  isSet: boolean;
  /** The replacement being typed. Empty means "keep what is stored". */
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  /** Optional consequence shown when replacing, e.g. "the old secret stops working immediately". */
  replaceWarning?: ReactNode;
}

/**
 * Write-only. A stored secret shows "محفوظ" and a Replace button — never a
 * reveal. Replacing opens an empty input; cancelling clears it and keeps the
 * stored value. When nothing is stored the input is open from the start and
 * the badge says so.
 */
export function SecretField({ label, hint, error, isSet, value, onChange, required = false, disabled = false, replaceWarning }: SecretFieldProps) {
  const [replacing, setReplacing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const open = !isSet || replacing;

  return (
    <Field label={label} hint={hint ?? (isSet ? ui('secretNeverShown') : undefined)} error={error} required={required && !isSet}>
      {(control) =>
        open ? (
          <div className="secret">
            <div className="input-wrap" data-ltr>
              <span className="input-affix">
                <LockKey size={16} aria-hidden="true" />
              </span>
              <input
                ref={inputRef}
                className="input mono"
                dir="ltr"
                type="password"
                autoComplete="new-password"
                spellCheck={false}
                value={value}
                disabled={disabled}
                onChange={(event) => onChange(event.target.value)}
                {...control}
              />
            </div>
            <div className="secret-row">
              {isSet ? (
                <Button
                  size="sm"
                  variant="ghost"
                  icon={X}
                  onClick={() => {
                    onChange('');
                    setReplacing(false);
                  }}
                >
                  {ui('secretKeep')}
                </Button>
              ) : (
                <Badge tone="warning" dot>
                  {ui('secretNotSet')}
                </Badge>
              )}
              {replacing && replaceWarning !== undefined ? <span className="secret-warning">{replaceWarning}</span> : null}
            </div>
          </div>
        ) : (
          <div className="secret secret-stored">
            {/* The control's id lands on the read-only status so the label still points somewhere. */}
            <output className="secret-value" id={control.id} aria-describedby={control['aria-describedby']}>
              <Badge tone="success" icon={LockKey}>
                {ui('secretSet')}
              </Badge>
              <span className="secret-dots" aria-hidden="true">
                ••••••••••••
              </span>
            </output>
            <Button
              size="sm"
              icon={PencilSimple}
              disabled={disabled}
              onClick={() => {
                setReplacing(true);
                requestAnimationFrame(() => inputRef.current?.focus());
              }}
            >
              {ui('secretReplace')}
            </Button>
          </div>
        )
      }
    </Field>
  );
}
